// Pure helpers for FROM-list edit modules (add/remove table). Lives in
// edit/edits/ so multiple edit-action modules can share them.
//
// `formatRelationRef` writes a relation whose `quoted` marker says how it was
// typed (a schema-tree drag payload): quoted identifiers round-trip as
// double-quoted, the schema is prefixed when present. A table picked from the
// schema SNAPSHOT is a catalog spelling instead — `formatCatalogTableRef`
// writes it bare only when the engine reads the bare spelling back to exactly
// those bytes (`public."MidVqeMixed"`, `dbo.[Order Details]`).
//
// `tableSuggestionKey` and `tableSuggestionLabel` produce stable
// keys/labels for the table-chooser popover items list; tests assert their
// stability so re-render-on-suggestion-fetch doesn't reset list selection.
// The key is injective (`identifierKey`): the pickers look the picked table
// up by it, so `"a.b".c` and `a."b.c"` must never share one.

import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import { catalogRelationSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import { identifierKey } from '../../../../sql-resolver/service/snapshotRelations';
import type { Table } from '../../../../sql-resolver/types/schema';

export function formatRelationRef(qualified: QualifiedName): string {
  const namePart = formatIdent(qualified.name, qualified.quoted);
  if (qualified.schema) {
    const schemaPart = formatIdent(qualified.schema, false);
    return `${schemaPart}.${namePart}`;
  }
  return namePart;
}

/** SQL for a table picked from the schema snapshot (catalog spelling), dialect-exact. */
export function formatCatalogTableRef(table: Table, engine: SqlEngine): string {
  return catalogRelationSql(table.qualified, engine);
}

/** `schema.name` folded (`.name` without a schema), a dotted part quoted — see `identifierKey`. */
export function tableSuggestionKey(table: Table): string {
  return identifierKey(table.qualified.foldedSchema ?? '', table.qualified.foldedName);
}

/** `schema.name` as the catalog spells it; a part holding a `.` is shown quoted so `"a.b".c` and `a."b.c"` read apart. */
export function tableSuggestionLabel(table: Table): string {
  return table.qualified.schema
    ? identifierKey(table.qualified.schema, table.qualified.name)
    : identifierKey(table.qualified.name);
}

export function formatIdent(name: string, quoted: boolean): string {
  if (!quoted) return name;
  // Round-trip quoted identifiers as double-quoted (canonical SQL form).
  // Engine-specific bracket / backtick variants are out of scope for v1.
  return `"${name.replace(/"/g, '""')}"`;
}
